// controls.js — PointerLockControls + WASD/arrow movement, clamped to a
// safe walking AABB inside the room (avoiding walls, side display cases, the
// counter, and the back-wall Tincture kiosk).
import * as THREE from 'three';
import { PointerLockControls } from 'three/examples/jsm/controls/PointerLockControls.js';
import { ROOM } from './scene.js';

// Side display cases extend ~1m into the room from each side wall;
// counter + back-wall kiosks extend ~1m from the back wall.
const PAD = 0.55;
const X_MIN = -ROOM.W / 2 + 1.05 + PAD;
const X_MAX =  ROOM.W / 2 - 1.05 - PAD;
const Z_MIN = -ROOM.D / 2 + 1.05 + PAD;
const Z_MAX =  ROOM.D / 2 - PAD;
const EYE_HEIGHT = 1.65;

const SPEED = 3.4; // m/s — slightly faster for the bigger room

// Soft circular obstacles: chocolate fountain + gummy mountain.
// Player gets pushed out radially when they get within `radius` meters.
const OBSTACLES = [
  { x:  6.5, z:  4.5, radius: 0.95 }, // chocolate fountain
  { x: -6.5, z: -3.5, radius: 1.10 }, // gummy mountain
];

// Rectangular obstacles (counter island). Player gets pushed to the nearest
// outside edge when they cross into the AABB (with `pad` meters of margin so
// the camera doesn't clip through the box face).
const RECT_OBSTACLES = [
  // Counter — center cx=1.5, cz=-5.5, dims 4.2 × 0.9 → spans x[-0.6,3.6] z[-5.95,-5.05]
  { minX: -0.6, maxX: 3.6, minZ: -5.95, maxZ: -5.05, pad: 0.40 },
];

export function createControls(camera, domElement) {
  const controls = new PointerLockControls(camera, domElement);

  // Note: the start-overlay show/hide decision is made in main.js, not here —
  // it has to know whether a panel (cart/info) is open. If we auto-show the
  // overlay on every unlock, opening the cart with `C` would reveal the start
  // overlay on top of the cart drawer (it has higher z-index), making the
  // cart unclickable.

  const keys = { forward: false, back: false, left: false, right: false };
  const setKey = (code, down) => {
    switch (code) {
      case 'KeyW': case 'ArrowUp':    keys.forward = down; break;
      case 'KeyS': case 'ArrowDown':  keys.back    = down; break;
      case 'KeyA': case 'ArrowLeft':  keys.left    = down; break;
      case 'KeyD': case 'ArrowRight': keys.right   = down; break;
    }
  };
  window.addEventListener('keydown', (e) => setKey(e.code, true));
  window.addEventListener('keyup',   (e) => setKey(e.code, false));

  function update(dt) {
    if (!controls.isLocked) return;
    const obj = controls.getObject ? controls.getObject() : controls.object;

    const step = SPEED * dt;
    if (keys.forward) controls.moveForward( step);
    if (keys.back)    controls.moveForward(-step);
    if (keys.right)   controls.moveRight(   step);
    if (keys.left)    controls.moveRight(  -step);

    // Wall AABB clamp
    obj.position.x = THREE.MathUtils.clamp(obj.position.x, X_MIN, X_MAX);
    obj.position.z = THREE.MathUtils.clamp(obj.position.z, Z_MIN, Z_MAX);

    // Push out of circular obstacles (chocolate fountain, gummy mountain)
    for (const o of OBSTACLES) {
      const dx = obj.position.x - o.x;
      const dz = obj.position.z - o.z;
      const d2 = dx * dx + dz * dz;
      const r2 = o.radius * o.radius;
      if (d2 < r2 && d2 > 0) {
        const d = Math.sqrt(d2);
        const push = (o.radius - d) / d;
        obj.position.x += dx * push;
        obj.position.z += dz * push;
      }
    }

    // Push out of rectangular obstacles (counter island).
    for (const r of RECT_OBSTACLES) {
      const minX = r.minX - r.pad, maxX = r.maxX + r.pad;
      const minZ = r.minZ - r.pad, maxZ = r.maxZ + r.pad;
      if (obj.position.x <= minX || obj.position.x >= maxX) continue;
      if (obj.position.z <= minZ || obj.position.z >= maxZ) continue;
      const dL = obj.position.x - minX;
      const dR = maxX - obj.position.x;
      const dB = obj.position.z - minZ;
      const dF = maxZ - obj.position.z;
      const m = Math.min(dL, dR, dB, dF);
      if (m === dF)      obj.position.z = maxZ;
      else if (m === dB) obj.position.z = minZ;
      else if (m === dL) obj.position.x = minX;
      else               obj.position.x = maxX;
    }

    obj.position.y = EYE_HEIGHT;
  }

  return {
    object: controls.object,
    update,
    isLocked: () => controls.isLocked,
    // Imperative lock/unlock used by main.js for the click-to-enter flow.
    // controls.lock() requires a user-gesture context, so call this from a
    // real click handler.
    lockPointer: () => { try { controls.lock(); } catch (e) { /* gesture-required */ } },
    unlockPointer: () => controls.unlock(),
  };
}
